import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";

const ManualServiceHoursModal = ({
  isOpen,
  onClose,
  onSubmit,
  employee,
  locations,
}) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    locationId: employee?.locationId || "",
    clockInTime: "09:00",
    clockOutTime: "17:00",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const calculateHours = () => {
    if (!formData.clockInTime || !formData.clockOutTime) return 0;

    const [inHours, inMinutes] = formData.clockInTime.split(":").map(Number);
    const [outHours, outMinutes] = formData.clockOutTime.split(":").map(Number);

    const inTotalMinutes = inHours * 60 + inMinutes;
    const outTotalMinutes = outHours * 60 + outMinutes;

    const diffMinutes = outTotalMinutes - inTotalMinutes;
    return (diffMinutes / 60).toFixed(2);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.date) {
      newErrors.date = "Date is required";
    }

    if (!formData.locationId) {
      newErrors.locationId = "Location is required";
    }

    if (!formData.clockInTime) {
      newErrors.clockInTime = "Clock in time is required";
    }

    if (!formData.clockOutTime) {
      newErrors.clockOutTime = "Clock out time is required";
    }

    // Validate clock out is after clock in
    if (formData.clockInTime && formData.clockOutTime) {
      const hours = calculateHours();
      if (hours <= 0) {
        newErrors.clockOutTime = "Clock out time must be after clock in time";
      }
      if (hours > 24) {
        newErrors.clockOutTime = "Shift cannot exceed 24 hours";
      }
    }

    // Validate date is not in the future
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (selectedDate > today) {
      newErrors.date = "Date cannot be in the future";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      // Combine date and time
      const clockInDateTime = new Date(
        `${formData.date}T${formData.clockInTime}:00`,
      );
      const clockOutDateTime = new Date(
        `${formData.date}T${formData.clockOutTime}:00`,
      );

      const payload = {
        employeeId: employee.id,
        locationId: formData.locationId,
        clockInTime: clockInDateTime.toISOString(),
        clockOutTime: clockOutDateTime.toISOString(),
        totalHours: parseFloat(calculateHours()),
        notes: formData.notes || null,
        isManualEntry: true,
      };

      await onSubmit(payload);

      // Reset form
      setFormData({
        date: new Date().toISOString().split("T")[0],
        locationId: employee?.locationId || "",
        clockInTime: "09:00",
        clockOutTime: "17:00",
        notes: "",
      });

      onClose();
    } catch (err) {
      setErrors({ submit: err.message || "Failed to add service hours" });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const totalHours = calculateHours();

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[1200] animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[1201] w-full max-w-lg">
        <div className="bg-card rounded-xl border border-border shadow-xl animate-slide-in">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Clock" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">
                  Add Manual Service Hours
                </h2>
                <p className="text-sm text-muted-foreground">
                  {employee?.name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              {errors.submit && (
                <div className="alert alert-error">
                  <Icon name="AlertCircle" size={20} />
                  <span>{errors.submit}</span>
                </div>
              )}

              {/* Date */}
              <div>
                <label className="label">Date *</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange("date", e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                  error={errors.date}
                />
                {errors.date && (
                  <p className="text-sm text-error mt-1">{errors.date}</p>
                )}
              </div>

              {/* Location */}
              <div>
                <label className="label">Location *</label>
                <select
                  className="input"
                  value={formData.locationId}
                  onChange={(e) => handleChange("locationId", e.target.value)}
                >
                  <option value="">Select location</option>
                  {locations?.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
                {errors.locationId && (
                  <p className="text-sm text-error mt-1">{errors.locationId}</p>
                )}
              </div>

              {/* Clock In/Out Times */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Clock In *</label>
                  <Input
                    type="time"
                    value={formData.clockInTime}
                    onChange={(e) =>
                      handleChange("clockInTime", e.target.value)
                    }
                    error={errors.clockInTime}
                  />
                  {errors.clockInTime && (
                    <p className="text-sm text-error mt-1">
                      {errors.clockInTime}
                    </p>
                  )}
                </div>

                <div>
                  <label className="label">Clock Out *</label>
                  <Input
                    type="time"
                    value={formData.clockOutTime}
                    onChange={(e) =>
                      handleChange("clockOutTime", e.target.value)
                    }
                    error={errors.clockOutTime}
                  />
                  {errors.clockOutTime && (
                    <p className="text-sm text-error mt-1">
                      {errors.clockOutTime}
                    </p>
                  )}
                </div>
              </div>

              {/* Total Hours Display */}
              <div className="bg-muted/50 rounded-lg p-4 flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Total Hours:
                </span>
                <span className="text-2xl font-bold font-data text-primary">
                  {totalHours}h
                </span>
              </div>

              {/* Notes */}
              <div>
                <label className="label">Notes (Optional)</label>
                <textarea
                  className="input min-h-[80px]"
                  placeholder="Add any notes about this shift..."
                  value={formData.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.notes.length}/500 characters
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                iconName="Save"
                disabled={loading}
              >
                {loading ? "Adding..." : "Add Service Hours"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ManualServiceHoursModal;
